import Component from "@glimmer/component";
import { concat } from "@ember/helper";
import { getOwner } from "@ember/owner";
import { service } from "@ember/service";
import { type ComponentLike } from "@glint/template";
import { modifier } from "ember-modifier";
import DFloatBody from "discourse/float-kit/components/d-float-body";
import {
  type FloatCallback,
  TOOLTIP,
  type TooltipOptions,
} from "discourse/float-kit/lib/constants";
import DTooltipInstance from "discourse/float-kit/lib/d-tooltip-instance";
import type TooltipService from "discourse/float-kit/services/tooltip";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dIcon from "discourse/ui-kit/helpers/d-icon";

/** The object yielded to each of the tooltip's blocks. */
export interface DTooltipComponentArgs<Data = unknown> {
  /** Closes the tooltip. */
  close: FloatCallback;

  /** The `@data` passed to the tooltip. */
  data?: Data;

  /** Whether the tooltip is currently open — reflects the live instance state. */
  expanded: boolean;
}

// The subset of arguments that mirror a tooltip's option bag. Built as a
// `Partial<Omit<…>>` over `TooltipOptions` (defined in `constants.ts`), so the
// arguments track the options automatically: a field added there is accepted here
// for free, with no second list to keep in sync. The three omitted fields are
// re-declared to narrow their generic `TooltipOptions` types (`data: unknown`, a
// `Data`-agnostic `component`, `onRegisterApi: FloatCallback | null`) to the
// component's `Data` and the concrete `DTooltipInstance`.
type DTooltipOptionArgs<Data> = Partial<
  Omit<TooltipOptions, "data" | "component" | "onRegisterApi">
> & {
  /** The data passed to the content block and rendered component. */
  data?: Data;

  /** A component rendered as the content; it receives the `@data` and `@close` arguments. */
  component?: ComponentLike<{ Args: { data?: Data; close?: FloatCallback } }>;

  /** Called with the tooltip instance when it is created, so callers can control it programmatically. */
  onRegisterApi?: (instance: DTooltipInstance) => void;
};

interface DTooltipSignature<Data = unknown> {
  Element: HTMLSpanElement;
  Args: DTooltipOptionArgs<Data> & {
    // Arguments the component reads directly and forwards to the trigger button;
    // these are not keys of `TOOLTIP.options`.

    /** The icon ID for the default trigger button. */
    icon?: string;

    /** The label for the default trigger button. */
    label?: string;
  };
  Blocks: {
    /** The tooltip content; takes precedence over the `content` block. Yields the tooltip api. */
    default: [DTooltipComponentArgs<Data>];

    /** Rendered as the trigger, replacing the default button. Yields the tooltip api. */
    trigger: [DTooltipComponentArgs<Data>];

    /** The tooltip content, used when no default block is given. Yields the tooltip api. */
    content: [DTooltipComponentArgs<Data>];
  };
}

/**
 * The declarative tooltip component: it renders a trigger (an icon and/or label
 * by default, or a `trigger` block) and, while open, the tooltip content in a
 * positioned float. It creates and owns its own `DTooltipInstance`, populating
 * the instance options from its arguments, so a template can drop in a tooltip
 * without touching the `tooltip` service. For a tooltip whose trigger is managed
 * separately through that service, see `DHeadlessTooltip`.
 */
export default class DTooltip<Data = unknown> extends Component<
  DTooltipSignature<Data>
> {
  @service declare tooltip: TooltipService;

  tooltipInstance = new DTooltipInstance(getOwner(this)!, {
    ...this.allowedProperties,
    autoUpdate: true,
    listeners: true,
  } as Partial<TooltipOptions>);

  registerTrigger = modifier((element: HTMLElement) => {
    this.tooltipInstance.trigger = element;
    this.options.onRegisterApi?.(this.tooltipInstance);

    return () => {
      this.tooltipInstance.destroy();
    };
  });

  get options(): TooltipOptions {
    return this.tooltipInstance?.options;
  }

  get componentArgs(): DTooltipComponentArgs<Data> {
    const instance = this.tooltipInstance;
    return {
      close: this.tooltip.close,
      data: this.options.data as Data,
      // A getter (not a snapshot) so a consumer reading `expanded` subscribes to the
      // live tracked state and re-renders on open/close, without churning this object.
      get expanded() {
        return instance.expanded;
      },
    };
  }

  get allowedProperties() {
    const properties: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(TOOLTIP.options)) {
      properties[key] = (this.args as Record<string, unknown>)[key] ?? value;
    }
    return properties;
  }

  <template>
    <span
      aria-expanded={{if this.tooltipInstance.expanded "true" "false"}}
      class={{dConcatClass
        "fk-d-tooltip__trigger"
        (if this.tooltipInstance.expanded "-expanded")
      }}
      data-identifier={{this.options.identifier}}
      data-trigger
      id={{this.tooltipInstance.id}}
      role="button"
      ...attributes
      {{this.registerTrigger this.allowedProperties}}
    >
      <span class="fk-d-tooltip__trigger-container">
        {{~#if (has-block "trigger")~}}
          {{yield this.componentArgs to="trigger"}}
        {{~else~}}
          {{#if @icon}}
            <span class="fk-d-tooltip__icon">
              {{~dIcon @icon~}}
            </span>
          {{/if}}
          {{#if @label}}
            <span class="fk-d-tooltip__label">{{@label}}</span>
          {{/if}}
        {{~/if~}}
      </span></span>
    {{~#if this.tooltipInstance.expanded~}}
      <DFloatBody
        @inline={{this.options.inline}}
        @innerClass="fk-d-tooltip__inner-content"
        @instance={{this.tooltipInstance}}
        @mainClass={{dConcatClass
          "fk-d-tooltip__content"
          (concat this.options.identifier "-content")
        }}
        @role="tooltip"
        @trapTab={{and this.options.interactive this.options.trapTab}}
      >
        {{#if (has-block)}}
          {{yield this.componentArgs}}
        {{else if (has-block "content")}}
          {{yield this.componentArgs to="content"}}
        {{else if this.options.component}}
          <this.options.component
            @close={{this.tooltipInstance.close}}
            @data={{this.options.data}}
          />
        {{else if this.options.content}}
          {{this.options.content}}
        {{/if}}
      </DFloatBody>
    {{~/if~}}
  </template>
}
